import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  UseGroupMembersConfig,
  UseGroupMembersReturn,
  Member,
  GlobalMember,
  GROUP_MEMBERS_CONSTANTS,
} from '@cobu/types';
import { Strings, URLS } from '../constants';
import { groupMembersService } from '@cobu/api-client';

// Query keys for caching
export const GROUP_MEMBERS_KEYS = {
  all: ['groupMembers'],
  building: (buildingId: string) => [...GROUP_MEMBERS_KEYS.all, 'building', buildingId],
  group: (groupId: string) => [...GROUP_MEMBERS_KEYS.all, 'group', groupId],
  buildingResidents: (buildingId: string) => [...GROUP_MEMBERS_KEYS.building(buildingId), 'residents'],
};

/**
 * Service functions are implemented in @src/services/groupMembers
 */

/**
 * Utility function to check if a member has a profile picture
 */
function hasProfilePicture(member: Member | GlobalMember): boolean {
  const profileImage = member.profileImage || (member as Member).user?.profileImage;
  return !!(profileImage && profileImage !== URLS.DEFAULT_PROFILE_IMAGE);
}

/**
 * Utility function to filter and sort members
 */
function processMembers(
  members: Member[] | GlobalMember[], 
  config: UseGroupMembersConfig
): Member[] | GlobalMember[] {
  const { maxDisplay = GROUP_MEMBERS_CONSTANTS.DEFAULT_MAX_DISPLAY, prioritizePictures = true, filterResidentsOnly = true } = config;
  
  let filteredMembers = [...members];

  // Filter out deactivated and blocked members
  filteredMembers = filteredMembers.filter(member => 
    !member.deactivated && !member.blocked
  );

  // Filter residents only if requested
  if (filterResidentsOnly) {
    filteredMembers = filteredMembers.filter(member => 
      member.role?.value === 0 // RESIDENT role
    );
  }

  // Prioritize members with profile pictures
  if (prioritizePictures) {
    const membersWithPictures = filteredMembers.filter(hasProfilePicture);
    const membersWithoutPictures = filteredMembers.filter(member => !hasProfilePicture(member));
    
    // If we have enough members with pictures, use them
    if (membersWithPictures.length >= maxDisplay) {
      return membersWithPictures.slice(0, maxDisplay);
    }
    
    // Otherwise, combine both groups
    filteredMembers = [...membersWithPictures, ...membersWithoutPictures];
  }

  return filteredMembers.slice(0, maxDisplay);
}

/**
 * Custom hook for managing group members data and state
 * 
 * @param config Configuration object for data fetching and processing
 * @returns Hook return object with data, states, and actions
 * 
 * @example
 * ```tsx
 * const { displayMembers, totalCount, isLoading, hasOverflow } = useGroupMembers({
 *   buildingId: 'building-123',
 *   maxDisplay: 8,
 *   prioritizePictures: true,
 * });
 * ```
 */
export function useGroupMembers(config: UseGroupMembersConfig = {}): UseGroupMembersReturn {
  const {
    buildingId,
    groupId,
    enabled = true,
    staleTime = GROUP_MEMBERS_CONSTANTS.STALE_TIME,
  } = config;


  // Determine query configuration based on context
  const queryKey = groupId 
    ? GROUP_MEMBERS_KEYS.group(groupId)
    : buildingId 
      ? GROUP_MEMBERS_KEYS.buildingResidents(buildingId)
      : GROUP_MEMBERS_KEYS.all;

  const queryFn = async () => {
    if (groupId) {
      return groupMembersService.getGroupMembers(groupId);
    } else if (buildingId) {
      return groupMembersService.getBuildingMembers(buildingId);
    } else {
      throw new Error('Either buildingId or groupId must be provided');
    }
  };

  // Main data query
  const query = useQuery({
    queryKey,
    queryFn,
    enabled: enabled && !!(buildingId || groupId),
    staleTime,
    gcTime: staleTime * 2,
    retry: (failureCount, error: any) => {
      // Don't retry on permission errors
      if (error?.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Memoized computed values
  const computedValues = useMemo(() => {
    const members = query.data || [];
    const totalCount = members.length;
    const displayMembers = processMembers(members, config);
    const visibleCount = displayMembers.length;
    const overflowCount = Math.max(0, totalCount - visibleCount);

    return {
      members,
      displayMembers,
      totalCount,
      visibleCount,
      overflowCount,
      hasMembers: members.length > 0,
      hasOverflow: overflowCount > 0,
      isEmpty: members.length === 0,
    };
  }, [query.data, config]);

  return {
    // Data
    ...computedValues,
    
    // States
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error?.message || null,
    
    // Actions
    refetch: query.refetch,
  };
}

export default useGroupMembers; 